import { useEffect, useState, useMemo, Fragment } from 'react'
import { Box, Stack, Text, VStack, Divider } from '@chakra-ui/react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Row, Collapse } from 'reactstrap'
import ProcessMediaTab from 'components/ProcessMediaTab'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import { ITextContentWithRelations } from 'interfaces/textContent'
import { checkValidArray, getValidArray } from 'utils/common'
import CardHeader from './components/CardHeader'
import MediaView from './components/MediaView'
import TaskCard from './components/TaskCard'
import styles from './stepCard.module.scss'

interface IStepCardProps {
  lastStep: boolean
  step: IStepWithRelations
  isCommonStepFromStepId?: number
  onToggleMessageView?: Function
  procedureId: number
  isDragging: boolean
  searchText?: string
  alwayExpand?: boolean
  isReviewMode?: boolean
  reviewStatusMap?: Record<string, { needsReview: boolean; isReviewed: boolean }>
  onToggleReviewItem?: (itemId: string) => void
}

const StepCard = ({
  step,
  lastStep,
  searchText,
  procedureId,
  isDragging,
  onToggleMessageView = () => {},
  alwayExpand,
  isCommonStepFromStepId,
  isReviewMode = false,
  reviewStatusMap = {},
  onToggleReviewItem = () => {}
}: IStepCardProps) => {
  const { processStore } = useStores()

  const [textContents, setTextContents] = useState<ITextContentWithRelations[]>([])
  const expandingSteps = processStore.expandingSteps || []
  const isOpen = alwayExpand || expandingSteps.includes(step.id)

  const mediaList: IMedia[] = step?.media || []
  const [selectedMedia, setSelectedMedia] = useState<IMedia>(mediaList[0] ? mediaList[0] : ({} as IMedia))

  const hasMedia: boolean = checkValidArray(mediaList)
  const hasTextContent: boolean = checkValidArray(step?.textContents)

  useEffect(() => {
    setTextContents(step?.textContents ?? [])
  }, [step])

  // Check if this step has blocks that need review
  const stepId = step?._id || step?.id
  const hasBlocksToReview = useMemo(() => {
    if (!isReviewMode || !stepId) return false
    return Object.keys(reviewStatusMap).some(itemId => itemId.startsWith(`step-${stepId}-`))
  }, [isReviewMode, stepId, reviewStatusMap])

  function toggleExpand(): void {
    if (isOpen) {
      const currentStepIndex = expandingSteps.findIndex(expandingId => expandingId === step.id)
      let newExpandedSteps = [...expandingSteps]
      newExpandedSteps.splice(currentStepIndex, 1)
      processStore.setExpandingSteps(newExpandedSteps)
    } else {
      processStore.setExpandingSteps([...expandingSteps, step.id])
    }
  }

  return (
    <Row className={cx(styles.container, isDragging && styles.isDragging)}>
      <CardHeader
        onClick={alwayExpand ? () => {} : toggleExpand}
        position={step.position}
        name={step.name}
        mediaList={mediaList}
        searchText={searchText}
        step={step}
        lastStep={lastStep}
        procedureId={procedureId}
        className={styles.cardHeader}
        isCollapsed={!isOpen}
        hasBlocksToReview={hasBlocksToReview}
      />
      {hasTextContent || hasMedia ? (
        <Collapse className={styles.collapse} isOpen={isOpen}>
          <Stack
            width="full"
            height="full"
            align="flex-start"
            justify="space-between"
            borderTop="1px solid #E2E8F0"
            flexDirection={{ base: 'column', md: 'row' }}
            spacing={0}
          >
            <VStack width="full" align="flex-start" padding={{ base: 4, sm: 6 }} spacing={3}>
              {hasMedia && !hasTextContent ? (
                <VStack width="full" spacing={3}>
                  <MediaView
                    mediaList={mediaList}
                    setSelectedMedia={setSelectedMedia}
                    mediaIndex={getValidArray(mediaList).findIndex(media => media?.id === selectedMedia?.id)}
                  />
                </VStack>
              ) : (
                <VStack width="full" spacing={0}>
                  {Array.isArray(textContents) &&
                    textContents.map((textContent: ITextContentWithRelations, index: number) => (
                      <Fragment key={`textContent-${textContent.id || index}`}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            background: '#FFFFFF',
                            width: '100%'
                          }}
                        >
                          <div className={cx(styles.cardFullWidth, styles.cardSpacingRight, styles.cardSpacingLeft)}>
                            <TaskCard
                              key={`taskCard-${textContent.id || index}`}
                              task={textContent}
                              mediaList={mediaList}
                              selectedMedia={selectedMedia}
                              setSelectedMedia={setSelectedMedia}
                              isReviewMode={isReviewMode}
                              reviewStatusMap={reviewStatusMap}
                              onToggleReviewItem={onToggleReviewItem}
                              stepId={step?._id || step?.id}
                            />
                          </div>
                        </div>
                        {index < textContents.length - 1 && (
                          <Divider 
                            borderColor="gray.200" 
                            marginY="16px" 
                            width="100%" 
                          />
                        )}
                      </Fragment>
                    ))}
                </VStack>
              )}
            </VStack>
            {hasMedia && !isReviewMode && (
              <Box maxWidth={{ md: '212px' }} width="full" height="full" borderLeft="2px solid #E2E8F0">
                <ProcessMediaTab
                  isEditing={false}
                  setShowManageMediaDialog={() => {}}
                  mediaList={mediaList}
                  selectedMedia={selectedMedia}
                  setSelectedMedia={setSelectedMedia}
                  step={step}
                  hasTextContent={hasTextContent}
                  isOpenEmbedLinkModal={false}
                  closeEmbedLinkModal={() => {}}
                  isEditingMedia={false}
                  setIsEditingMedia={() => {}}
                />
              </Box>
            )}
          </Stack>
        </Collapse>
      ) : (
        <Collapse className={styles.collapse} isOpen={isOpen}>
          <Text margin={{ base: 4, sm: 6 }}>Empty drill</Text>
        </Collapse>
      )}
    </Row>
  )
}

export default observer(StepCard)
